import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "@/contexts/AppContext";
import { Eye, EyeOff, Loader2, ArrowLeft, Mail, Check } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import logoFull from "@/assets/logo-full.jpeg";

type Step = "login" | "signup" | "forgot";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LoginPage = () => {
  const { login, signup, loginAsGuest } = useApp();
  const { toast } = useToast();
  const [guestLoading, setGuestLoading] = useState(false);
  const [deviceConflict, setDeviceConflict] = useState<{ message: string; isAnonymous: boolean } | null>(null);
  const [step, setStep] = useState<Step>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState("");

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("auth") === "signup") setStep("signup");
    } catch {}
  }, []);

  const handleGuest = async () => {
    setGuestLoading(true);
    try { await loginAsGuest(); }
    catch (e: any) { if (e?.code === "device_has_account") setDeviceConflict({ message: e.message, isAnonymous: !!e.isAnonymous }); }
    finally { setGuestLoading(false); }
  };

  const validateEmail = (val: string) => {
    const n = val.toLowerCase().trim();
    setEmail(n);
    setEmailError(n && !EMAIL_REGEX.test(n) ? "Please enter a valid email address" : "");
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_REGEX.test(email)) { setEmailError("Please enter a valid email address"); return; }
    setSubmitting(true);
    try { await login(email, password); } catch {} finally { setSubmitting(false); }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!EMAIL_REGEX.test(email)) { setEmailError("Please enter a valid email address"); return; }
    if (password.length < 6) return;
    if (!agreedTerms) return;
    setSubmitting(true);
    try {
      // Username will be set during onboarding — pass a temp derived from email.
      const tempUsername = email.split("@")[0].replace(/[^a-z0-9]/gi, "").toLowerCase().slice(0, 16) || `user${Date.now()}`;
      await signup(email, password, tempUsername);
    } catch (e: any) {
      if (e?.code === "device_has_account") setDeviceConflict({ message: e.message, isAnonymous: !!e.isAnonymous });
    } finally { setSubmitting(false); }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError("");
    if (!EMAIL_REGEX.test(resetEmail)) { setResetError("Please enter a valid email address"); return; }
    setSubmitting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    try {
      const { data: prof } = await supabase.from("profiles").select("user_id").eq("email", resetEmail.trim().toLowerCase()).maybeSingle();
      void supabase.functions.invoke("send-email", {
        body: { type: "password_reset", to: resetEmail, user_id: prof?.user_id, idempotency_key: `pwreset-${resetEmail}-${Date.now()}`, data: { reset_url: `${window.location.origin}/reset-password` } },
      });
    } catch {}
    setSubmitting(false);
    if (error) { setResetError(error.message); return; }
    setResetSent(true);
    toast({ title: "Reset email sent!", description: "Check your inbox for the password reset link." });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute top-20 -right-20 w-72 h-72 rounded-full bg-primary/[0.05] blur-[100px] pointer-events-none" />
      <div className="absolute bottom-20 -left-20 w-60 h-60 rounded-full bg-accent/[0.05] blur-[100px] pointer-events-none" />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm relative z-10">
        <div className="text-center mb-8 relative">
          <div className="absolute inset-0 blur-2xl opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(168,85,247,0.3), rgba(236,72,153,0.2), transparent)", transform: "scale(1.3)" }} />
          <img src={logoFull} alt="TalkLot" className="h-28 mx-auto object-contain relative z-10" style={{ mixBlendMode: "screen" }} />
          <p className="text-sm text-muted-foreground mt-3 relative z-10">Connect with the world</p>
        </div>

        <AnimatePresence mode="wait">
          {step === "login" && (
            <motion.form key="login" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} onSubmit={handleLogin} className="space-y-3.5">
              <div>
                <input type="email" placeholder="Email" value={email} onChange={(e) => validateEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-muted/30 border border-border/15 text-foreground placeholder:text-muted-foreground/50 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30" required />
                {emailError && <p className="text-destructive text-[10px] mt-1 px-1">{emailError}</p>}
              </div>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-muted/30 border border-border/15 text-foreground placeholder:text-muted-foreground/50 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30 pr-12" required minLength={6} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/50">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-right">
                <button type="button" onClick={() => setStep("forgot")} className="text-primary text-xs font-medium">Forgot password?</button>
              </div>
              <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={submitting}
                className="w-full py-3 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Log In
              </motion.button>
            </motion.form>
          )}

          {step === "signup" && (
            <motion.form key="signup" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} onSubmit={handleSignup} className="space-y-3.5">
              <div className="text-center mb-2">
                <h2 className="font-display font-bold text-foreground text-lg">Create your account</h2>
                <p className="text-xs text-muted-foreground mt-1">Just email and password to get started</p>
              </div>
              <div>
                <input type="email" placeholder="Email" value={email} onChange={(e) => validateEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-muted/30 border border-border/15 text-foreground placeholder:text-muted-foreground/50 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30" required />
                {emailError && <p className="text-destructive text-[10px] mt-1 px-1">{emailError}</p>}
              </div>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} placeholder="Password (min 6 characters)" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-muted/30 border border-border/15 text-foreground placeholder:text-muted-foreground/50 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30 pr-12" required minLength={6} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/50">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="flex items-start gap-2.5 mt-1">
                <Checkbox id="terms" checked={agreedTerms} onCheckedChange={(c) => setAgreedTerms(c === true)} className="mt-0.5" />
                <label htmlFor="terms" className="text-[11px] text-muted-foreground leading-snug cursor-pointer">
                  I agree to TalkLot{" "}
                  <Link to="/terms" className="text-primary underline font-medium" target="_blank">Terms & Conditions and Privacy Policy</Link>
                </label>
              </div>
              <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={submitting || !agreedTerms || password.length < 6 || !EMAIL_REGEX.test(email)}
                className="w-full py-3 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Create Account
              </motion.button>
              <p className="text-center text-muted-foreground/60 mt-4 text-xs">
                Already have an account?{" "}
                <button type="button" onClick={() => setStep("login")} className="text-primary font-semibold">Log In</button>
              </p>
            </motion.form>
          )}

          {step === "forgot" && (
            <motion.div key="forgot" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <h2 className="font-display font-bold text-foreground text-lg">Reset Password</h2>
                <p className="text-xs text-muted-foreground mt-1">{resetSent ? "Check your email for the reset link" : "Enter your email to receive a reset link"}</p>
              </div>
              {resetSent ? (
                <div className="text-center space-y-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                    <Check className="w-6 h-6 text-emerald-400" />
                  </div>
                  <p className="text-sm text-muted-foreground">We sent a reset link to <span className="text-foreground font-medium">{resetEmail}</span></p>
                  <motion.button whileTap={{ scale: 0.98 }} onClick={() => { setStep("login"); setResetSent(false); setResetEmail(""); }}
                    className="w-full py-3 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm">Back to Login</motion.button>
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-3.5">
                  <div>
                    <input type="email" placeholder="Your email address" value={resetEmail} onChange={(e) => { setResetEmail(e.target.value); setResetError(""); }}
                      className="w-full px-4 py-3 rounded-xl bg-muted/30 border border-border/15 text-foreground placeholder:text-muted-foreground/50 text-sm focus:outline-none focus:ring-1 focus:ring-primary/30" required />
                    {resetError && <p className="text-destructive text-[10px] mt-1 px-1">{resetError}</p>}
                  </div>
                  <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={submitting}
                    className="w-full py-3 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Send Reset Link
                  </motion.button>
                  <button type="button" onClick={() => setStep("login")} className="w-full text-center text-muted-foreground text-xs mt-2">
                    <ArrowLeft className="w-3 h-3 inline mr-1" /> Back to login
                  </button>
                </form>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {step === "login" && (
          <>
            <motion.button whileTap={{ scale: 0.98 }} onClick={() => setStep("signup")} type="button"
              className="w-full mt-3 py-3 rounded-xl bg-muted/30 border border-primary/30 text-foreground font-display font-semibold text-sm flex items-center justify-center gap-2 hover:bg-muted/50 transition-colors">
              Create Account
            </motion.button>
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-border/30" />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">or</span>
              <div className="flex-1 h-px bg-border/30" />
            </div>
            <motion.button whileTap={{ scale: 0.98 }} onClick={handleGuest} disabled={guestLoading || submitting} type="button"
              className="w-full py-3 rounded-xl bg-muted/30 border border-border/30 text-foreground font-display font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2 hover:bg-muted/50 transition-colors">
              {guestLoading && <Loader2 className="w-4 h-4 animate-spin" />} Continue as Guest
            </motion.button>
            <p className="text-center text-[10px] text-muted-foreground/50 mt-2 px-4">Try TalkLot instantly. Create an account later to save your progress.</p>
          </>
        )}
      </motion.div>

      <AlertDialog open={!!deviceConflict} onOpenChange={(o) => !o && setDeviceConflict(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Account already on this device</AlertDialogTitle>
            <AlertDialogDescription>{deviceConflict?.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setDeviceConflict(null); setStep("login"); }}>Log In Instead</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LoginPage;
